import React, { FC, useCallback, useContext, useMemo, useState } from 'react';

import BasicButton from '../../../components/buttons/basicButton';
// components
import AampeTooltip from '../../../components/shared/AampeTooltip/AampeTooltip';

// constants
import { CONTROLLED_FORMULA_BUFFER } from '../../../constants/formula';
import { FormulaType } from '../../../constants/formulaType';

// contexts
import { EditorContext } from '../../../context/EditorContext';

// helpers
import { isScheduleTimeInPast } from '../../../helpers/formulaHelpersPure';

import useFormulaApprove from '../../../hooks/formulas/useFormulaApprove';
// hooks
import useHandleIntegrationRequest from '../../../hooks/helper/useHandleIntegrationRequest';
import { useAuthSession } from '../../../hooks/useAuthSession';

// models
import { FormulaTypesData } from '../../../models/formula.model';

import ApprovedSuccessModal from '../formulaApprove/ApprovedSuccessModal';

interface FormulaApproveActionProps {
    buttonText: string;
    buttonTooltipText?: string;
    disabled?: boolean;
    isPrimary?: boolean;
}

const FormulaApproveAction: FC<
    React.PropsWithChildren<FormulaApproveActionProps>
> = ({ buttonText, buttonTooltipText, disabled, isPrimary = true }) => {
    const {
        editorDetails: { previews, formula, formulaType },
    } = useContext(EditorContext);

    const { user: currentUser } = useAuthSession();
    const customerId = currentUser?.customerId;

    const { handleIntegrationRequest, requestLoading } =
        useHandleIntegrationRequest(formulaType as FormulaType);
    const messageApprove = useFormulaApprove(formula?.id);

    const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

    const isControlledFormula = useMemo(
        () => !!formula?.is_controlled_message,
        [formula],
    );

    const isChannelIntegrated = useMemo<boolean>(
        () =>
            !!formulaType &&
            !!FormulaTypesData[formulaType].isIntegrated?.(customerId),
        [formulaType, customerId],
    );
    const isScheduledTimeInPast = useMemo(() => {
        return (
            isControlledFormula &&
            isScheduleTimeInPast(
                formula?.send_start as string,
                CONTROLLED_FORMULA_BUFFER,
            )
        );
    }, [formula, isControlledFormula]);

    const handleMessageApprove = useCallback(() => {
        if (isChannelIntegrated && formula?.id) {
            messageApprove.mutate(formula?.id, {
                onSuccess: () => {
                    setShowSuccessModal(true);
                },
            });
        } else {
            handleIntegrationRequest?.();
        }
    }, [
        formula,
        messageApprove,
        isChannelIntegrated,
        handleIntegrationRequest,
    ]);

    return (
        <>
            <AampeTooltip
                title={
                    isScheduledTimeInPast
                        ? `Controlled message can only be scheduled at least ${CONTROLLED_FORMULA_BUFFER} minutes in future.`
                        : buttonTooltipText ||
                          'Pressing this button will schedule this Message Group to start sending (within the audience, trigger, and time constraints you’ve set) to eligible users.'
                }
                arrow
            >
                <div>
                    <BasicButton
                        isLoading={messageApprove.isPending || requestLoading}
                        name={buttonText}
                        isPrimary={isPrimary}
                        action={() => handleMessageApprove()}
                        disabled={
                            disabled ||
                            isScheduledTimeInPast ||
                            !previews?.length
                        }
                    />
                </div>
            </AampeTooltip>
            {showSuccessModal && (
                <ApprovedSuccessModal handleClose={setShowSuccessModal} />
            )}
        </>
    );
};

export default FormulaApproveAction;
